import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { QuestionViewGraphTypeV1 } from "./questionViewGraphTypeV1";
/* eslint-disable import/export */
export abstract class UpdateQuestionGraphTypeRPC implements
  LeoRPC<UpdateQuestionGraphTypeRPC.Request, UpdateQuestionGraphTypeRPC.Response, UpdateQuestionGraphTypeRPC.Errors.Errors>
{
  abstract execute(
    request: UpdateQuestionGraphTypeRPC.Request
  ): Promise<LeoRPCResult<UpdateQuestionGraphTypeRPC.Response, UpdateQuestionGraphTypeRPC.Errors.Errors>>;
}

export namespace UpdateQuestionGraphTypeRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
    surveyViewId: string;
    surveyQuestionViewId: string;
    questionViewGraphType: QuestionViewGraphTypeV1.QuestionViewGraphTypeV1DTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isUpdateQuestionGraphTypeRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly surveyViewId: LeoUUID;
    readonly surveyQuestionViewId: LeoUUID;
    readonly questionViewGraphType: QuestionViewGraphTypeV1.QuestionViewGraphTypeV1;
  
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID, 
    surveyViewId: LeoUUID, 
    surveyQuestionViewId: LeoUUID, 
    questionViewGraphType: QuestionViewGraphTypeV1.QuestionViewGraphTypeV1
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
      this.surveyViewId = surveyViewId;
      this.surveyQuestionViewId = surveyQuestionViewId;
      this.questionViewGraphType = questionViewGraphType;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const surveyViewId = getLeoUuid(dto, "surveyViewId", InvalidRequestSurveyViewIdError);
      const surveyQuestionViewId = getLeoUuid(dto, "surveyQuestionViewId", InvalidRequestSurveyQuestionViewIdError);
      const questionViewGraphType = QuestionViewGraphTypeV1.fromDTO(getObject(dto, "questionViewGraphType", InvalidRequestQuestionViewGraphTypeError));
      return new Request(
        projectId,
        surveyId,
        surveyViewId,
        surveyQuestionViewId,
        questionViewGraphType
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          surveyViewId: this.surveyViewId.toDTO(),
          surveyQuestionViewId: this.surveyQuestionViewId.toDTO(),
          questionViewGraphType: this.questionViewGraphType.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `UpdateQuestionGraphTypeRPC(projectId='${this.projectId}',surveyId='${this.surveyId}',surveyViewId='${this.surveyViewId}',surveyQuestionViewId='${this.surveyQuestionViewId}',questionViewGraphType='${this.questionViewGraphType}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `UpdateQuestionGraphTypeRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidSurveyViewId = "INVALID_SURVEY_VIEW_ID",
    InvalidQuestionViewId = "INVALID_QUESTION_VIEW_ID",
    InvalidGraphType = "INVALID_GRAPH_TYPE"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `UpdateQuestionGraphTypeRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `UpdateQuestionGraphTypeRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `UpdateQuestionGraphTypeRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidSurveyViewIdDTO {
      code: string;
    }
    
    export class InvalidSurveyViewId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyViewId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyViewId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyViewIdDTO {
        const dto: InvalidSurveyViewIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyViewId.prototype.toString = function toString(): string {
      return `UpdateQuestionGraphTypeRPC.INVALID_SURVEY_VIEW_ID()`;
    };
    
    
    export interface InvalidQuestionViewIdDTO {
      code: string;
    }
    
    export class InvalidQuestionViewId extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionViewId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionViewId;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionViewIdDTO {
        const dto: InvalidQuestionViewIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionViewId.prototype.toString = function toString(): string {
      return `UpdateQuestionGraphTypeRPC.INVALID_QUESTION_VIEW_ID()`;
    };
    
    
    export interface InvalidGraphTypeDTO {
      code: string;
    }
    
    export class InvalidGraphType extends LeoRPCError<RPCError> {
      readonly isInvalidGraphType: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidGraphType;
        super(code);
      }
    
    
      toDTO(): InvalidGraphTypeDTO {
        const dto: InvalidGraphTypeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidGraphType.prototype.toString = function toString(): string {
      return `UpdateQuestionGraphTypeRPC.INVALID_GRAPH_TYPE()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | ProjectAlreadyArchived | InvalidSurveyViewId | InvalidQuestionViewId | InvalidGraphType;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | ProjectAlreadyArchivedDTO | InvalidSurveyViewIdDTO | InvalidQuestionViewIdDTO | InvalidGraphTypeDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidSurveyViewId:
          return new InvalidSurveyViewId();
        case RPCError.InvalidQuestionViewId:
          return new InvalidQuestionViewId();
        case RPCError.InvalidGraphType:
          return new InvalidGraphType();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends UpdateQuestionGraphTypeRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends UpdateQuestionGraphTypeRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestSurveyViewIdError extends UpdateQuestionGraphTypeRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyViewIdError";
  }
}

export class InvalidRequestSurveyQuestionViewIdError extends UpdateQuestionGraphTypeRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyQuestionViewIdError";
  }
}

export class InvalidRequestQuestionViewGraphTypeError extends UpdateQuestionGraphTypeRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuestionViewGraphTypeError";
  }
}


export class InvalidResponseInvalidProjectIdError extends UpdateQuestionGraphTypeRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends UpdateQuestionGraphTypeRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends UpdateQuestionGraphTypeRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidSurveyViewIdError extends UpdateQuestionGraphTypeRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyViewIdError";
  }
}

export class InvalidResponseInvalidQuestionViewIdError extends UpdateQuestionGraphTypeRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionViewIdError";
  }
}

export class InvalidResponseInvalidGraphTypeError extends UpdateQuestionGraphTypeRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidGraphTypeError";
  }
}

